/*
 * LES LISTES DE L'ANCIENNE BORNE, relevées le 20/10/2026 dans les formulaires
 * repris (≈ 440 demandes de 09/2025 à 10/2026 qui les portent). La première
 * valeur de chaque liste est celle que l'ancien formulaire proposait par
 * défaut ; l'ordre des suivantes n'a pas pu être relevé. Une valeur que
 * personne n'a jamais choisie a pu échapper au relevé : elle s'ajoute ici, et
 * l'API comme l'écran la proposent aussitôt.
 */
export const TYPES_COMPTABILITE = ['Engagement', 'Trésorerie'];
export const REGIMES_TVA = ['Annuel - CA12', 'Mensuel - CA3', 'Trimestriel - CA3', 'Franchise de base', 'Non assujetti'];
export const EXIGIBILITES_VENTES = ['Mixtes', 'Sur les débits', 'Sur les encaissements'];
export const REGIMES_FISCAUX = [
    'IS - RN',
    'IS - RS',
    'IR - BIC - RN',
    'IR - BIC - RS',
    'IR - BNC',
    'IR - BA - RN',
    'IR - BA - RS',
    'IR - BA-Micro',
    'IR - SCI-S',
    'IR - SCI-C',
    'IR - RPF',
];
export const PLANS_COMPTABLES = ['Agricole', 'Général', 'CSE'];
export const MISSIONS_PRESENTATION = ['Avec tenue comptable', 'Sans tenue comptable'];
/** Un formulaire vierge, avec les valeurs par défaut de l'ancienne borne. */
export function dossierInqomVide() {
    return {
        codeDossier: '',
        formeJuridique: '',
        denomination: '',
        nomCommercial: '',
        siret: '',
        codeNaf: '',
        adresse: '',
        complement: '',
        codePostal: '',
        ville: '',
        dirigeantNom: '',
        dirigeantPrenom: '',
        ecSignataire: '',
        site: '',
        manager: '',
        cm: '',
        cc: '',
        typeComptabilite: TYPES_COMPTABILITE[0],
        regimeTva: REGIMES_TVA[0],
        exigibilite: EXIGIBILITES_VENTES[0],
        regimeFiscal: REGIMES_FISCAUX[0],
        planComptable: PLANS_COMPTABLES[0],
        debutTravail: '',
        missionPresentation: MISSIONS_PRESENTATION[0],
        exerciceDebut: '',
        exerciceFin: '',
        creerN1: true,
        creerN2: true,
    };
}
/**
 * Le formulaire est proposé aux COLLABORATEURS DU GROUPE — comptables
 * d'Altonéo et équipe d'IT Solutions —, jamais à un client externe : c'est
 * une demande du cabinet, pas de son client.
 */
export function peutDemanderDossierInqom(kind) {
    return kind === 'staff' || kind === 'collaborateur';
}
/** Les champs que le client choisi remplit ; les autres restent ceux de la saisie. */
export function preremplirDossierInqom(s, k) {
    return {
        ...s,
        codeDossier: k.codeDossier,
        denomination: k.denomination,
        nomCommercial: k.nomCommercial ?? s.nomCommercial,
        formeJuridique: k.formeJuridique ?? s.formeJuridique,
        siret: k.siret ?? s.siret,
        codeNaf: k.codeNaf ?? s.codeNaf,
        adresse: k.adresse ?? s.adresse,
        complement: k.complement ?? s.complement,
        codePostal: k.codePostal ?? s.codePostal,
        ville: k.ville ?? s.ville,
        site: k.site ?? s.site,
    };
}
/** Un exercice comptable dure 24 mois au plus (premier exercice allongé compris). */
const MOIS_EXERCICE_MAX = 24;
/**
 * Ce qui empêche le formulaire de partir, champ par champ (règle 9 : dire quoi
 * corriger). Obligatoire : ce sans quoi le dossier ne se crée pas dans Inqom —
 * l'identité et le premier exercice. Le reste se vérifie s'il est saisi : un
 * SIRET pas encore attribué ne bloque pas une création.
 */
export function refusDossierInqom(s) {
    const r = [];
    const manque = (champ, message) => {
        if (!String(s[champ] ?? '').trim())
            r.push({ champ, message });
    };
    manque('formeJuridique', 'Indiquez la forme juridique (SARL, SAS, SCI…).');
    manque('denomination', 'Indiquez la dénomination sociale.');
    manque('debutTravail', 'Indiquez à partir de quelle date vous travaillerez sur le dossier.');
    manque('exerciceDebut', 'Indiquez le début du premier exercice.');
    manque('exerciceFin', 'Indiquez la fin du premier exercice.');
    const chiffres = s.siret.replace(/\s/g, '');
    if (chiffres && !/^\d{14}$/.test(chiffres)) {
        r.push({ champ: 'siret', message: 'Un SIRET compte 14 chiffres : vérifiez-le, ou laissez-le vide s’il n’est pas encore attribué.' });
    }
    if (s.codeNaf.trim() && !/^\d{2}\.?\d{2}[A-Za-z]$/.test(s.codeNaf.trim())) {
        r.push({ champ: 'codeNaf', message: 'Code NAF attendu sous la forme 6920Z.' });
    }
    if (s.codePostal.trim() && !/^\d{5}$/.test(s.codePostal.trim())) {
        r.push({ champ: 'codePostal', message: 'Un code postal compte 5 chiffres.' });
    }
    if (s.exerciceDebut && s.exerciceFin) {
        if (s.exerciceFin <= s.exerciceDebut) {
            r.push({ champ: 'exerciceFin', message: 'La fin de l’exercice doit suivre son début : vérifiez les deux dates.' });
        }
        else {
            const [a1, m1] = s.exerciceDebut.split('-').map(Number);
            const [a2, m2] = s.exerciceFin.split('-').map(Number);
            if ((a2 - a1) * 12 + (m2 - m1) >= MOIS_EXERCICE_MAX) {
                r.push({ champ: 'exerciceFin', message: `Un exercice dure ${MOIS_EXERCICE_MAX} mois au plus : vérifiez sa fin.` });
            }
        }
    }
    return r;
}
const jour = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
/** L'objet de la demande : la file distingue les créations, et dit lequel. */
export function objetDossierInqom(s) {
    return `Création de dossier Inqom — ${s.denomination.trim()}`.slice(0, 120);
}
/**
 * Le contenu de la demande, une ligne « libellé : valeur » par champ, aux
 * libellés de l'ancienne borne : l'intervenant lit de la même façon une
 * demande reprise et une demande nouvelle. Écrit une fois ici : l'API et le
 * mode démo l'appellent.
 */
export function texteDossierInqom(s) {
    const ouiNon = (b) => (b ? 'Oui' : 'Non');
    const lignes = [
        ['Code dossier', s.codeDossier],
        ['Forme juridique', s.formeJuridique],
        ['Dénomination sociale', s.denomination],
        ['Nom Commercial', s.nomCommercial],
        ['SIRET', s.siret.replace(/\s/g, '')],
        ['Code NAF', s.codeNaf.toUpperCase()],
        ['Adresse du siège social', s.adresse],
        ['Complément', s.complement],
        ['Code postal', s.codePostal],
        ['Ville', s.ville],
        ['Nom du dirigeant', s.dirigeantNom],
        ['Prénom du dirigeant', s.dirigeantPrenom],
        ['EC signataire', s.ecSignataire],
        ['Site', s.site],
        ['Manager', s.manager],
        ['CM', s.cm],
        ['CC', s.cc],
        ['Type de comptabilité', s.typeComptabilite],
        ['Régime de TVA', s.regimeTva],
        ['Exigibilité sur les ventes', s.exigibilite],
        ['Régime fiscal', s.regimeFiscal],
        ['Plan comptable', s.planComptable],
        ['A partir de quelle date travaillerez-vous sur le dossier', jour(s.debutTravail)],
        ['Mission de présentation des comptes annuels', s.missionPresentation],
        ['Exercice du', `${jour(s.exerciceDebut)} au ${jour(s.exerciceFin)}`],
        ['Créer N-1', ouiNon(s.creerN1)],
        ['Créer N-2', ouiNon(s.creerN2)],
    ];
    return ['CRÉATION DE DOSSIER INQOM', ...lignes.map(([l, v]) => `${l} : ${v.trim()}`)].join('\n');
}
//# sourceMappingURL=inqom.js.map